import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Link } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ClassifiersConfigProvider } from './contexts/ClassifiersConfigContext';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Families from './pages/Families';
import FamilyDetail from './pages/FamilyDetail';
import MemberDetail from './pages/MemberDetail';
import DocumentView from './pages/DocumentView';
import AdminConfig from './pages/AdminConfig';
import AdminUsers from './pages/AdminUsers';
import AdminClassifications from './pages/AdminClassifications';

import PrivateRoute from './components/PrivateRoute';
import HomeRoute from './pages/HomeRoute';
import ShareAccept from './pages/ShareAccept';
import SharedMembers from './pages/SharedMembers';
import E2eeUnlockModalHost from './components/E2eeUnlockModalHost';
import ServiceWorkerUpdateBanner from './components/ServiceWorkerUpdateBanner';
import './App.css';

const HeartMark: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
  </svg>
);

const AppFooter: React.FC = () => {
  const location = useLocation();
  if (location.pathname === '/') return null;
  const year = new Date().getFullYear();
  const version = process.env.REACT_APP_VERSION || 'dev';
  return (
    <footer className="app-footer" role="contentinfo">
      <div className="app-footer__inner">
        <Link to="/" className="app-footer__brand" aria-label="eDosarMedical">
          <HeartMark />
          <span>eDosar<strong>Medical</strong></span>
        </Link>
        <span className="app-footer__meta">
          <span>© {year}</span>
          <span className="app-footer__dot" aria-hidden>·</span>
          <span className="app-footer__version">v{version}</span>
        </span>
      </div>
    </footer>
  );
};

function App() {
  return (
    <AuthProvider>
      <ClassifiersConfigProvider>
      <Router>
        <div className="App">
          <ServiceWorkerUpdateBanner />
          <Routes>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/share/:token" element={<ShareAccept />} />
            <Route
              path="/shared-members"
              element={
                <PrivateRoute>
                  <SharedMembers />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/families"
              element={
                <PrivateRoute>
                  <Families />
                </PrivateRoute>
              }
            />
            <Route
              path="/families/:familyId"
              element={
                <PrivateRoute>
                  <FamilyDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/members/:memberId"
              element={
                <PrivateRoute>
                  <MemberDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/documents/:documentId"
              element={
                <PrivateRoute>
                  <DocumentView />
                </PrivateRoute>
              }
            />
            {/* New admin routes: /admin/users + /admin/system.
                /admin/config kept as alias so old bookmarks still work. */}
            <Route
              path="/admin/users"
              element={
                <PrivateRoute>
                  <AdminUsers />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/classifications"
              element={
                <PrivateRoute>
                  <AdminClassifications />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/system"
              element={
                <PrivateRoute>
                  <AdminConfig />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/config"
              element={
                <PrivateRoute>
                  <AdminConfig />
                </PrivateRoute>
              }
            />
          </Routes>
          <AppFooter />

        </div>
        <E2eeUnlockModalHost />
      </Router>
      </ClassifiersConfigProvider>
    </AuthProvider>
  );
}

export default App;
